// Refusals, with the message in the reader's language. English first, Danish written natively.
// Moves to `@adea/i18n` once it exists (b1-platform); the codes and details stay.

import type { ActionError, ActionErrorCode, ActionFailure, ErrorDetails, Locale } from "./spec";
import { ERROR_STATUS } from "./status";

/** Throw from a handler to refuse with a typed code: `throw new ActionRefusal("not_found")`. */
export class ActionRefusal extends Error {
  readonly code: ActionErrorCode;
  readonly details: unknown;
  /** Overrides the standard message. Write it to the person reading it, in their language. */
  readonly readerMessage: string | undefined;

  constructor(code: ActionErrorCode, options: { details?: unknown; message?: string } = {}) {
    super(options.message ?? code);
    this.name = "ActionRefusal";
    this.code = code;
    this.details = options.details;
    this.readerMessage = options.message;
  }
}

/** What each plan feature is and which plans have it, for the sentence a refusal gives (`PLANS` has the facts). */
const PLAN_NEEDS: Record<string, { en: string; da: string }> = (() => {
  const startUp = (en: string, da: string, be = "are") => ({
    en: `${en} ${be} part of Start and up.`,
    da: `${da} hører til Start og opefter.`,
  });
  const proUp = (en: string, da: string, be = "are") => ({
    en: `${en} ${be} part of Pro and up.`,
    da: `${da} hører til Pro og opefter.`,
  });
  const business = (en: string, da: string, be = "are") => ({
    en: `${en} ${be} part of Business.`,
    da: `${da} hører til Business.`,
  });
  const access = proUp("Access rules and masking", "Adgangsregler og skjulte tal");
  const brand = proUp(
    "Public links, embeds and your own brand",
    "Offentlige links, indlejring og jeres eget udtryk",
  );
  const approvals = business(
    "Approvals and your own safety rules",
    "Godkendelser og jeres egne sikkerhedsregler",
  );
  return {
    explore: {
      en: "Free shows an answer's headline and chart. The full table, and changing the period or filters, come with a trial or a paid plan.",
      da: "Gratis viser et svars overskrift og diagram. Hele tabellen og at skifte periode eller filtre hører til en prøveperiode eller et betalt abonnement.",
    },
    now_push: startUp("Alerts on your phone", "Beskeder på telefonen"),
    now_slack: startUp("Alerts in Slack", "Beskeder i Slack"),
    now_teams: startUp("Alerts in Teams", "Beskeder i Teams"),
    now_sms: proUp("Text messages", "Sms'er"),
    now_call: proUp("Phone calls", "Telefonopkald"),
    brief_per_scope: proUp(
      "A brief for each location or team",
      "Et overblik for hver lokation eller hvert team",
      "is",
    ),
    access_rules: access,
    masking: access,
    sso: proUp("Single sign-on", "Single sign-on", "is"),
    public_links: brand,
    embed: brand,
    brand,
    guardian_approvals: approvals,
    guardian_rules: approvals,
    log_export: business("Exporting the security log", "Eksport af sikkerhedsloggen", "is"),
    sign_in_rule: business("A sign-in rule for the team", "En loginregel for teamet", "is"),
    warehouses: business("Warehouses", "Datavarehuse"),
    code: startUp("Connecting your code", "At forbinde jeres kode", "is"),
    insights_why: startUp(
      "The why behind an insight, and its lists",
      "Hvorfor bag en indsigt og dens lister",
      "is",
    ),
    lists: startUp("Lists on insights", "Lister på indsigter"),
    standing_lists: startUp("Standing lists", "Faste lister"),
    investigator: startUp("Find out why", "Find ud af hvorfor", "is"),
    // A company whose domain already used its free account: analysis starts with a plan.
    analysis: {
      en: "This company has already had its free account, so questions start with a plan.",
      da: "Virksomheden har allerede haft sin gratis konto, så spørgsmål starter med et abonnement.",
    },
  };
})();

type Messages = {
  [C in ActionErrorCode]: (details: C extends keyof ErrorDetails ? ErrorDetails[C] : unknown) => string;
};

const en: Messages = {
  invalid_input: () => "Some of the details aren't valid. Check them and try again.",
  unauthenticated: () => "Sign in to continue.",
  plan_required: (d) =>
    d?.limit === "sources"
      ? `Your plan connects up to ${d.max ?? 0} ${d.max === 1 ? "source" : "sources"}. An administrator can start a trial or move to a bigger plan under Plan and billing to add more.`
      : d?.limit === "watchedNumbers"
        ? `Your plan watches up to ${d.max ?? 0} numbers. Stop watching one first, or an administrator can start a trial or move to a bigger plan under Plan and billing.`
        : `${PLAN_NEEDS[d?.feature ?? ""]?.en ?? "This isn't part of your plan."} An administrator can start a trial or upgrade under Plan and billing.`,
  usage_limit: () => "You've used this month's analysis. It renews on the 1st, or an admin can add more.",
  busy: () => "Adea is working on a lot for you right now. Try again in a moment.",
  cancelled: () => "This was stopped, so it didn't run.",
  forbidden: () => "You don't have access to do this. Ask an administrator if you need it.",
  guardian_held: () =>
    "This needs an administrator's approval. We've asked them, and you'll hear back as soon as they answer.",
  confirmation_invalid: () => "That confirmation has expired or doesn't match. Confirm again.",
  unknown_action: () => "That isn't something Adea can do.",
  not_available_here: () => "This can't be done from here. Open Adea to do it.",
  not_found: () => "We couldn't find that. It may have been deleted, or you may not have access to it.",
  conflict: () => "This changed while you were working on it. Reload and try again.",
  confirmation_required: () => "Confirm this before it runs.",
  rate_limited: () => "That's a lot at once. Wait a minute and try again.",
  internal: () => "Something went wrong on our side. Try again in a moment.",
  guardian_unavailable: () =>
    "We can't run the safety check on this right now, so it hasn't run. Try again in a moment.",
};

const da: Messages = {
  invalid_input: () => "Nogle af oplysningerne er ikke gyldige. Tjek dem, og prøv igen.",
  unauthenticated: () => "Log ind for at fortsætte.",
  plan_required: (d) =>
    d?.limit === "sources"
      ? `Jeres abonnement giver plads til ${d.max ?? 0} ${d.max === 1 ? "datakilde" : "datakilder"}. En administrator kan starte en prøveperiode eller skifte til et større abonnement under Abonnement og betaling, så I kan tilføje flere.`
      : d?.limit === "watchedNumbers"
        ? `Med jeres abonnement holder Adea øje med op til ${d.max ?? 0} tal. Stop med at følge et tal først, eller bed en administrator om at starte en prøveperiode eller skifte abonnement under Abonnement og betaling.`
        : `${PLAN_NEEDS[d?.feature ?? ""]?.da ?? "Det er ikke en del af jeres abonnement."} En administrator kan starte en prøveperiode eller skifte abonnement under Abonnement og betaling.`,
  usage_limit: () =>
    "I har brugt denne måneds analyse. Den fornyes den 1., eller en administrator kan tilføje mere.",
  busy: () => "Adea arbejder på meget for jer lige nu. Prøv igen om lidt.",
  cancelled: () => "Det blev stoppet, så det er ikke kørt.",
  forbidden: () =>
    "Du har ikke adgang til at gøre det her. Spørg en administrator, hvis du har brug for det.",
  guardian_held: () =>
    "Det her kræver godkendelse fra en administrator. Vi har spurgt dem, og du får besked, så snart de svarer.",
  confirmation_invalid: () => "Bekræftelsen er udløbet eller passer ikke. Bekræft igen.",
  unknown_action: () => "Det er ikke noget, Adea kan gøre.",
  not_available_here: () => "Det kan ikke gøres herfra. Åbn Adea for at gøre det.",
  not_found: () => "Vi kunne ikke finde det. Det kan være slettet, eller du har måske ikke adgang til det.",
  conflict: () => "Det blev ændret, mens du arbejdede på det. Genindlæs, og prøv igen.",
  confirmation_required: () => "Bekræft det her, før det kører.",
  rate_limited: () => "Det er meget på én gang. Vent et minut, og prøv igen.",
  internal: () => "Noget gik galt hos os. Prøv igen om lidt.",
  guardian_unavailable: () =>
    "Vi kan ikke sikkerhedstjekke det her lige nu, så det er ikke kørt. Prøv igen om lidt.",
};

/** sv, de, fr and es are switched off at launch; they read English until their copy pass. */
const catalogs: Partial<Record<Locale, Messages>> = { en, da };

export function messageFor(code: ActionErrorCode, locale: Locale, details?: unknown): string {
  const catalog = catalogs[locale] ?? en;
  return (catalog[code] as (d: unknown) => string)(details);
}

export function failure(
  code: ActionErrorCode,
  locale: Locale,
  details?: unknown,
  message?: string,
): ActionFailure {
  const error: ActionError = { code, message: message ?? messageFor(code, locale, details) };
  if (details !== undefined) error.details = details;
  return { ok: false, status: ERROR_STATUS[code], error };
}
